import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { editStatus } from '../../redux/state/status';
import classNames from 'classnames';
import { ChatInterface } from '../../utils/interfaces';
import {
    getLatestLoginHyperlinkMessageId,
    isMobileApiLoginUrl,
    parseUsedLoginHyperlinkMessageIds,
} from '../../utils/usedLoginHyperlink';

interface HyperlinkProps {
    url?: string;
    text: string;
    transactionId?: string;
    messageId?: string;
}

const HyperlinkText = (props: HyperlinkProps) => {
    let dispatch = useDispatch();

    const queueStatus = useSelector( (state: { status: { queueStatus: String } }) => state.status.queueStatus );
    
    const botButtonColor = useSelector(
        (state: { settings: { botButtonColor: string } }) =>
            state.settings.botButtonColor
    );

    const intentFontSize = useSelector(
        (state: { settings: { intentFontSize: number } }) =>
            state.settings.intentFontSize
    );

    const usedLoginHyperlinkMessageIds = useSelector(
        (state: { session: { usedLoginHyperlinkMessageIds?: string[] | string } }) =>
            state.session.usedLoginHyperlinkMessageIds
    );

    const chatMode = useSelector(
        (state: { session: { chatMode: string } }) => state.session.chatMode
    );

    const chats = useSelector((state: { chat: ChatInterface[] }) => state.chat);


    const fontSizesRef = ['xs', 'sm', 'base', 'lg', 'xl'];
    const smallFontSizeRef = ['2xs', 'xs', 'sm', 'base', 'lg'];
    const fontSizes = [
        fontSizesRef[0 + intentFontSize],
        fontSizesRef[1 + intentFontSize],
        fontSizesRef[2 + intentFontSize],
    ];
    const smallFontSizes = [
        smallFontSizeRef[0 + intentFontSize],
        smallFontSizeRef[1 + intentFontSize],
        smallFontSizeRef[2 + intentFontSize],
    ];

    const openLoginModal = () => {
        dispatch(
            editStatus({
                name: 'pendingLoginHyperlinkMessageId',
                value: props.messageId || '',
            })
        );
        dispatch(
            editStatus({
                name: 'isLoginModal',
                value: true,
            })
        );
    };

    const openLoginIframeModal = () => {
        dispatch(
            editStatus({
                name: 'isLoginIframeModal',
                value: true,
            })
        );
        dispatch(
            editStatus({
                name: 'loginIframeUrl',
                value: props.url,
            })
        );
    };

    const openNewTab = () => {
        window.open(props.url)
    };

    const openUploadFileModal = (receiptType: string) => {
        dispatch(editStatus({name: "transactionId", value: props.transactionId}))
        dispatch(editStatus({name: "receiptType", value: receiptType}))
        dispatch(
            editStatus({
                name: 'isUploadFileModal',
                value: true,
            })
        )
    }

    const urlString = props.url;
    const isLoginHyperlink = isMobileApiLoginUrl(urlString);
    const latestLoginHyperlinkMessageId = getLatestLoginHyperlinkMessageId(chats);
    const isLatestLoginHyperlink = Boolean(
        isLoginHyperlink &&
        props.messageId &&
        latestLoginHyperlinkMessageId &&
        props.messageId === latestLoginHyperlinkMessageId
    );
    const isUsedLoginHyperlink = Boolean(
        isLoginHyperlink &&
        props.messageId &&
        parseUsedLoginHyperlinkMessageIds(usedLoginHyperlinkMessageIds).includes(
            props.messageId
        )
    );

    if (
        isLoginHyperlink &&
        (chatMode === 'agent' || !isLatestLoginHyperlink || isUsedLoginHyperlink)
    ) {
        return null;
    }

    let onClickHandler: (() => void) | null = null;

    if (queueStatus === 'queueingDialog' || queueStatus === 'queueLoading') {
        // Do nothing → keep onClickHandler as null
    } else if (isLoginHyperlink) {
        onClickHandler = openLoginModal;
    } else if (urlString?.includes("uploadDepositReceipt")) {
        onClickHandler = () => openUploadFileModal('deposit');
    } else if (urlString?.includes("uploadWithdrawalReceipt")) {
        onClickHandler = () => openUploadFileModal('withdrawal');
    } else if (urlString?.includes("Login")) {
        onClickHandler = openLoginIframeModal;
    } else {
        // Default: open in new tab
        onClickHandler = openNewTab;
    }

    return(
        <a
            onClick={onClickHandler ?? undefined} // no effect if null
            className={classNames(
                `text-white font-medium rounded-lg text-${fontSizes[0]} small:text-${fontSizes[0]} medium:text-${fontSizes[1]} large:text-${fontSizes[2]} px-2 py-1.5 m-1 text-left inline-flex break-all`,
                {
                    "bg-sky-500 hover:bg-sky-700": !botButtonColor,
                    "cursor-not-allowed opacity-50": !onClickHandler, // show disabled style
                }
            )}
            style={{ backgroundColor: botButtonColor?.slice(0, 7) }}
        >
            {props.text}
        </a>
    );
};

HyperlinkText.propTypes = {};

export default HyperlinkText;
